import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProjectFormData, OTHER_OPTION_ID } from '../schemas/projectFormSchema';
import { ProjectTypeDtoIn } from '@/models/in/ProjectTypeDtoIn';

interface ProjectDescriptionStepProps {
  projectTypes: ProjectTypeDtoIn[];
  isLoadingTypes: boolean;
}

/**
 * The red star the portal already uses for mandatory fields (see FundPaymentSetup). aria-hidden
 * because the inputs carry aria-required themselves - read aloud, "star" is noise.
 */
const RequiredMark = () => <span className="text-red-500" aria-hidden="true"> *</span>;

/**
 * Step 1 of 3 — "תיאור המיזם": the name, selectable project-type cards (the closed list plus
 * "אחר") and the long description. Labels come from the admin-managed list, so a new type needs
 * no code change.
 *
 * The cards are radios, not checkboxes: exactly one type can be chosen, and a square checkbox
 * read as multi-select. Selection is shown the way the rest of the portal shows a chosen tile —
 * a stronger border and heavier label — rather than by a control glyph.
 */
const ProjectDescriptionStep = ({ projectTypes, isLoadingTypes }: ProjectDescriptionStepProps) => {
  const { t } = useTranslation();
  const { watch, setValue, register, formState: { errors } } = useFormContext<ProjectFormData>();

  const selectedTypeId = watch('projectTypeId');
  const isOtherSelected = selectedTypeId === OTHER_OPTION_ID;

  const handleSelectType = (id: number) => {
    setValue('projectTypeId', id, { shouldValidate: true });
    // Clear the free text when a listed type is chosen, so only one of the two is ever sent.
    if (id !== OTHER_OPTION_ID) setValue('projectTypeOther', '');
  };

  const options = [
    ...projectTypes.map((projectType) => ({ id: projectType.id, label: projectType.name })),
    { id: OTHER_OPTION_ID, label: t('projects.wizard.step1.otherType') },
  ];

  return (
    <div className="w-full flex flex-col gap-6">
      <h3 className="heading-h3-semibold text-(--font-primary)">{t('projects.wizard.step1.title')}</h3>

      {/* The name sits first (it moved here from step 3 at the office's request). */}
      <div className="flex flex-col gap-1">
        <label htmlFor="project-name" className="body-md-semibold text-(--font-secondary)">
          {t('projects.wizard.step1.nameLabel')}
          <RequiredMark />
        </label>
        <input
          id="project-name"
          type="text"
          aria-required
          aria-invalid={!!errors.name}
          className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
          placeholder={t('projects.wizard.step1.namePlaceholder')}
          {...register('name')}
        />
        {errors.name && <p className="text-red-500 body-sm-regular">{errors.name.message}</p>}
      </div>

      <fieldset className="flex flex-col gap-3">
        <legend className="body-md-semibold text-(--font-secondary) mb-2">
          {t('projects.wizard.step1.projectTypeLabel')}
          <RequiredMark />
        </legend>

        {/* The closed list ships empty and the office fills it from the management screen, so
            zero types is a normal state. The design does not cover it; without this hint the
            lone "אחר" card reads as a screen that failed to load. */}
        {!isLoadingTypes && projectTypes.length === 0 && (
          <p className="body-sm-regular text-(--font-secondary)">
            {t('projects.wizard.step1.noTypesHint')}
          </p>
        )}

        {isLoadingTypes ? (
          <p className="body-sm-regular text-(--font-secondary)">{t('loading')}</p>
        ) : (
          <div className="grid grid-cols-2 max-md:grid-cols-1 gap-3" role="radiogroup">
            {options.map((option) => {
              const isSelected = selectedTypeId === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => handleSelectType(option.id)}
                  className={`text-start rounded-lg px-4 py-3 transition-colors ${
                    isSelected
                      ? 'border-[1.5px] border-(--border-secondary-strong) bg-(--surface-base)'
                      : 'border border-(--border-lowest) bg-(--surface-base) hover:border-(--border-strong)'
                  }`}
                >
                  <span className={isSelected ? 'body-md-semibold' : 'body-md-regular'}>{option.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {errors.projectTypeId && (
          <p className="text-red-500 body-sm-regular">{errors.projectTypeId.message}</p>
        )}

        {isOtherSelected && (
          <div className="flex flex-col gap-1 mt-1">
            <input
              type="text"
              className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
              placeholder={t('projects.wizard.step1.otherTypePlaceholder')}
              {...register('projectTypeOther')}
            />
            {errors.projectTypeOther && (
              <p className="text-red-500 body-sm-regular">{errors.projectTypeOther.message}</p>
            )}
          </div>
        )}
      </fieldset>

      <div className="flex flex-col gap-1">
        <label htmlFor="project-description" className="body-md-semibold text-(--font-secondary)">
          {t('projects.wizard.step1.descriptionLabel')}
          <RequiredMark />
        </label>
        <textarea
          id="project-description"
          rows={6}
          aria-required
          aria-invalid={!!errors.description}
          className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular resize-none"
          placeholder={t('projects.wizard.step1.descriptionPlaceholder')}
          {...register('description')}
        />
        {errors.description && <p className="text-red-500 body-sm-regular">{errors.description.message}</p>}
      </div>
    </div>
  );
};

export default ProjectDescriptionStep;
